import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types/content'

import { cn } from '@/lib/utils'
import { cms, hasValue } from '@/lib/cms'

interface TestimonialCardProps {
    testimonial: Testimonial
    showMedia?: boolean
    className?: string
}

// One real, released testimonial: quote, optional result line, optional video and before/after pair.
export const TestimonialCard: FC<TestimonialCardProps> = ({ testimonial, showMedia = false, className }): ReactNode => {
    const hasPair = hasValue(testimonial.vorher) && hasValue(testimonial.nachher)

    return (
        <figure className={cn('flex flex-col gap-6 border-t border-ink-line pt-7', className)}>
            {showMedia && hasValue(testimonial.video) && (
                <div className='relative aspect-[9/16] w-full max-w-[22rem] overflow-hidden bg-ink-soft'>
                    <video
                        src={testimonial.video}
                        poster={testimonial.portrait ?? undefined}
                        controls
                        playsInline
                        preload='metadata'
                        aria-label={`Video-Erfahrungsbericht von ${testimonial.name}`}
                        className='absolute inset-0 size-full object-cover'
                    />
                </div>
            )}

            {showMedia && hasPair && (
                <div className='grid grid-cols-2 gap-2'>
                    {[
                        { src: testimonial.vorher as string, alt: testimonial.vorher_alt, label: 'Vorher' },
                        { src: testimonial.nachher as string, alt: testimonial.nachher_alt, label: 'Nachher' }
                    ].map(image => (
                        <div key={image.label} className='relative aspect-[4/5] overflow-hidden bg-ink-soft'>
                            <img
                                src={image.src}
                                alt={image.alt || `${image.label}: ${testimonial.name}`}
                                loading='lazy'
                                decoding='async'
                                className='absolute inset-0 size-full object-cover'
                            />
                            <span className='type-label absolute left-3 top-3 rounded-full bg-ink/70 px-2 py-1 text-bone backdrop-blur-sm'>
                                {image.label}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {hasValue(testimonial.ergebnis) && (
                <p className='type-label flex items-center gap-2 text-ember-bright'>
                    <span aria-hidden='true' className='h-px w-6 bg-current' />
                    <span data-mdlfy-cms={cms('erfahrungen', testimonial.id, 'ergebnis')}>{testimonial.ergebnis}</span>
                </p>
            )}

            {hasValue(testimonial.zitat) && (
                <blockquote>
                    <p
                        data-mdlfy-cms={cms('erfahrungen', testimonial.id, 'zitat')}
                        className="text-xl leading-snug text-bone before:content-['„'] after:content-['“'] sm:text-[1.375rem]"
                    >
                        {testimonial.zitat}
                    </p>
                </blockquote>
            )}

            <figcaption className='mt-auto flex items-center gap-4'>
                {hasValue(testimonial.portrait) && (
                    <img
                        src={testimonial.portrait}
                        alt={testimonial.portrait_alt || testimonial.name}
                        loading='lazy'
                        decoding='async'
                        className='size-12 shrink-0 rounded-full object-cover'
                    />
                )}

                <div className='flex flex-col gap-0.5'>
                    <span data-mdlfy-cms={cms('erfahrungen', testimonial.id, 'name')} className='font-semibold text-bone'>
                        {testimonial.name}
                    </span>

                    {hasValue(testimonial.kontext) && (
                        <span data-mdlfy-cms={cms('erfahrungen', testimonial.id, 'kontext')} className='text-sm text-ash'>
                            {testimonial.kontext}
                        </span>
                    )}
                </div>
            </figcaption>
        </figure>
    )
}

interface TestimonialPlaceholderProps {
    className?: string
}

// Designed empty slot shown while no released testimonial exists. Never a fake review.
export const TestimonialPlaceholder: FC<TestimonialPlaceholderProps> = ({ className }): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-4 border border-dashed border-ink-line p-6 sm:p-7', className)}>
            <span className='type-label text-ember-bright'>
                Platzhalter · nicht veröffentlichungsreif
            </span>

            <p className='type-display text-display-sm text-bone/70'>
                Erfahrungsbericht folgt
            </p>

            <p className='text-sm leading-relaxed text-ash'>
                Hier erscheint ein echter Erfahrungsbericht, sobald er im CMS unter Erfahrungen angelegt, mit Einwilligung versehen und veröffentlicht ist.
            </p>
        </div>
    )
}
